import {
  Table, TableBody, TableCell, TableContainer,
  TableFooter,
  TableHead,
  TableRow
} from '@one-thd/sui-atomic-components';
import PropTypes from 'prop-types';
// @ts-expect-error missing prop definition
import useControlled from '@one-thd/sui-atomic-components/dist/private/hooks/useControlled';
import get from 'lodash.get';
import React, { useMemo } from 'react';
import { DataGridHeaderCell } from './DataGridHeaderCell';
import { DataGridInnerRows } from './DataGridInnerRows';
import { DataGridData, DataGridProps } from './types';

// Hack around propTypes breaking generic interfaces
type DataGridComponent = {
  <TData extends DataGridData>(props: DataGridProps<TData>): React.JSX.Element;
  // eslint-disable-next-line @typescript-eslint/ban-types
  propTypes?: {};
  displayName: string;
};

/**
 * DataGrid component for rendering a table with customizable columns, rows, and various interactive features.
 *
 * @template TData - The type of data being displayed in the grid.
 *
 * @param {DataGridProps<TData>} props - The properties for the DataGrid component.
 * @returns {React.JSX.Element} The rendered DataGrid component.
 *
 * @prop {Array} columns - The columns configuration for the data grid.
 * @prop {Array} data - The data to be displayed in the grid.
 * @prop {string | function} dataId - The unique identifier for each data item.
 * @prop {string | object} density - The density of the table (tight, normal, loose).
 * @prop {boolean} loading - Indicates if the data is currently loading.
 * @prop {number} [skeletonRowCount=1] - The number of skeleton rows to display while loading.
 * @prop {function} onSort - Callback function for sorting columns.
 * @prop {Array<number | string>} checkedRows - The rows that are checked.
 * @prop {function} onRowClick - Callback function for row click events.
 * @prop {function} onColumnCheck - Callback function for column check events.
 * @prop {function} getRowProps - Function to get additional properties for each row.
 * @prop {object} expandable - Configuration for expandable rows.
 * @prop {function} onRowCheck - Callback function for row check events.
 * @prop {boolean} stickyHeader - Indicates if the header should be sticky.
 * @prop {string} layout - The layout of the table (auto, fixed).
 * @prop {React.ReactNode} footer - The footer content for the table.
 * @prop {object} tableContainerProps - Additional properties for the TableContainer component.
 * @prop {object} tableBodyProps - Additional properties for the TableBody component.
 * @prop {React.ComponentType} SkeletonComponent - The component to render for skeleton rows.
 * @prop {React.ComponentType} RowComponent - The component to render for each row.
 *
 */
const DataGrid: DataGridComponent = <TData extends DataGridData>(props: DataGridProps<TData>) => {
  const {
    columns,
    data,
    dataId,
    density,
    loading,
    skeletonRowCount = 1,
    onSort,
    checkedRows: _checkedRows,
    onRowClick,
    onColumnCheck,
    getRowProps,
    expandable,
    onRowCheck,
    stickyHeader,
    layout,
    footer,
    tableContainerProps,
    tableBodyProps,
    SkeletonComponent,
    RowComponent,
    getRowCellProps
  } = props;

  const getItemId = (item: TData, index: number): string | number => (typeof dataId === 'function' ? dataId(item, index) : get(item, dataId));

  const totalColSpan = useMemo(() => {
    const colSpanCount = columns.reduce((total, column) => total + (column.colSpan || 1), 0);

    if (expandable) {
      return colSpanCount + 1;
    }

    return colSpanCount;
  }, [columns]);

  const [checkedRows, setCheckedRows] = useControlled<(number | string)[]>({
    controlled: _checkedRows,
    defaultValue: []
  });

  const handleRowCheck = (event: React.ChangeEvent, rowIndex: (number | string)) => {
    event.stopPropagation();
    onRowCheck?.(event, rowIndex);
    if (checkedRows.includes(rowIndex)) {
      const newCheckedRows = checkedRows.filter((value: number | string) => value !== rowIndex);
      setCheckedRows([...newCheckedRows]);
    } else {
      setCheckedRows([...checkedRows, rowIndex]);
    }
  };

  const handleColumnCheck = (event: React.ChangeEvent, _isColumnChecked?: boolean) => {
    if (onColumnCheck) {
      onColumnCheck(event, _isColumnChecked as boolean);
    }
    const newCheckedRows = getNewCheckedRows(_isColumnChecked as boolean);
    setCheckedRows(newCheckedRows);
  };

  const getNewCheckedRows = (isColumnChecked: boolean) => {
    const ids = data.map((item, index) => getItemId(item, index));
    if (!isColumnChecked) {
      const newIds = ids.filter((id) => !checkedRows.includes(id));
      return [...checkedRows, ...newIds];
    }

    return checkedRows.filter((id: number | string) => !ids.includes(id));
  };

  const isIndeterminated = useMemo(() => {
    const allIdsChecked = data.every((item, index) => checkedRows.includes(getItemId(item, index)));
    const someIdsChecked = data.some((item, index) => checkedRows.includes(getItemId(item, index)));
    const isCurrentlyIndeterminated = !allIdsChecked && someIdsChecked;
    return isCurrentlyIndeterminated;
  }, [checkedRows, data]);

  const isColumnChecked = useMemo(() => {
    const isColumnCurrentlyChecked = data.every((item, index) => checkedRows.includes(getItemId(item, index)));
    return isColumnCurrentlyChecked;
  }, [checkedRows, data]);

  const densityValue = typeof density === 'object' ? density : { body: density, head: density };

  return (
    <>
      <TableContainer {...tableContainerProps}>
        <Table layout={layout} stickyHeader={stickyHeader} density={densityValue.body}>
          <TableHead density={densityValue.head}>
            <TableRow>
              {expandable && <TableCell />}
              {columns.map((column, index) => {
                const { key, sticky, headerCellProps } = column;

                return (
                  <DataGridHeaderCell
                    key={key}
                    column={column}
                    onSort={onSort}
                    data-testid="table-header"
                    onColumnCheck={handleColumnCheck}
                    isIndeterminated={isIndeterminated}
                    isChecked={isColumnChecked}
                    sticky={sticky ? 'horizontal' : undefined}
                    {...headerCellProps}
                  />
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody {...tableBodyProps}>
            <DataGridInnerRows
              columns={columns}
              data={data}
              dataId={dataId}
              loading={loading}
              checkedRows={checkedRows}
              skeletonRowCount={skeletonRowCount}
              onRowClick={onRowClick}
              onRowCheck={handleRowCheck}
              getRowProps={getRowProps}
              getRowCellProps={getRowCellProps}
              expandable={expandable}
              RowComponent={RowComponent}
              SkeletonComponent={SkeletonComponent}
              renderRowChildren={(item) => {
                // TODO: Add lazy loading
                const expandableItem = expandable?.renderExpandableItem({
                  item,
                  totalColSpan
                });

                const isExpanded = expandable?.isExpanded(item);
                if (isExpanded && expandableItem) {
                  return expandableItem;
                }

                // Default empty return
                return <></>;
              }}
            />
          </TableBody>
          {footer ? (
            <TableFooter colSpan={totalColSpan}>
              {footer}
            </TableFooter>
          ) : <></>}
        </Table>
      </TableContainer>
    </>
  );
};

// @ts-
DataGrid.propTypes = {
  checkedRows: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.number),
    PropTypes.arrayOf(PropTypes.string),
  ]),
  density: PropTypes.oneOfType([
    PropTypes.oneOf(['tight', 'normal', 'loose']),
    PropTypes.shape({
      body: PropTypes.oneOf(['tight', 'normal', 'loose']),
      head: PropTypes.oneOf(['tight', 'normal', 'loose']),
    })
  ]),
  expandable: PropTypes.shape({
    isExpanded: PropTypes.func,
    renderExpandableItem: PropTypes.func,
    isExpandable: PropTypes.func
  }),
  onColumnCheck: PropTypes.func,
  columns: PropTypes.arrayOf(PropTypes.shape({
    title: PropTypes.string,
    key: PropTypes.string,
    renderHeader: PropTypes.func,
    sortBy: PropTypes.oneOf(['asc', 'desc', null]),
    colSpan: PropTypes.number,
    align: PropTypes.oneOf(['left', 'right', 'center', 'justify']),
    enableSorting: PropTypes.bool,
    isNumber: PropTypes.bool,
    isCheckable: PropTypes.bool,
    renderCell: PropTypes.func,
    dataIndex: PropTypes.string,
    sticky: PropTypes.bool,
    tooltip: PropTypes.string,
    popover: PropTypes.shape({
      title: PropTypes.string.isRequired,
      content: PropTypes.node,
      placement: PropTypes.oneOf(['top', 'top-start', 'top-end', 'bottom', 'bottom-start', 'bottom-end', 'left', 'left-start', 'left-end', 'right', 'right-start', 'right-end']),
    }),
  })).isRequired,
  onSort: PropTypes.func,
  skeletonRowCount: PropTypes.number,
  onRowCheck: PropTypes.func,
  onRowClick: PropTypes.func,
  getRowProps: PropTypes.func,
  getRowCellProps: PropTypes.func,
  loading: PropTypes.bool,
  data: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  dataId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ]).isRequired,
  stickyHeader: PropTypes.bool,
  align: PropTypes.oneOf(['auto', 'fixed'])
};

DataGrid.displayName = 'DataGrid';

export { DataGrid };
